import React, { useState, useEffect, useMemo } from 'react';
import { useSidebar } from '../../contexts/SidebarContext';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import NotificationToast from '../../components/ui/NotificationToast';
import PayrollPeriodSelector from './components/PayrollPeriodSelector';
import PayrollTable from './components/PayrollTable';
import StatutoryComplianceSidebar from './components/StatutoryComplianceSidebar';
import PayrollActionBar from './components/PayrollActionBar';
import ValidationSummary from './components/ValidationSummary';
import CreatePayrollRecordModal from './components/CreatePayrollRecordModal';
import PayrollWorkflowBanner from './components/PayrollWorkflowBanner';
import EarningsBreakdownModal from './components/EarningsBreakdownModal';
import PaymentsBreakdownModal from './components/PaymentsBreakdownModal';
import DetailedPayslipModal from './components/DetailedPayslipModal';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import payrollService from '../../services/payrollService';
import employeeService from '../../services/employeeService';
import employeeDeploymentService from '../../services/employeeDeploymentService';
import attendanceService from '../../services/attendanceService';
import offerLettersService from '../../services/offerLettersService';
import {
  buildOfferCtcMap,
  findPayrollRecord,
  indexPayrollRecord,
  mapPayrollAmounts,
  resolveDeploymentEmployeeDbId,
} from '../../utils/payrollCalculations';

const PayrollManagement = () => {
  const { isCollapsed } = useSidebar();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [processingStatus, setProcessingStatus] = useState('pending');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [bulkProcessingStatus, setBulkProcessingStatus] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreatePayrollModal, setShowCreatePayrollModal] = useState(false);
  const [editingPayrollRecord, setEditingPayrollRecord] = useState(null);
  const [prefillEmployeeId, setPrefillEmployeeId] = useState(null);
  const [showEarningsModal, setShowEarningsModal] = useState(false);
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [showDetailedPayslipModal, setShowDetailedPayslipModal] = useState(false);
  const [selectedEmployeeForBreakdown, setSelectedEmployeeForBreakdown] = useState(null);

  const workflowCounts = useMemo(() => {
    const counts = { draft: 0, reviewed: 0, approved: 0, locked: 0 };
    payrollRecords.forEach((record) => {
      const status = record.status || 'draft';
      if (counts[status] !== undefined) counts[status]++;
    });
    return counts;
  }, [payrollRecords]);

  // Mock user data
  const user = {
    name: "HR Administrator",
    email: "hr@facilitypro.com",
    role: "HR Manager"
  };

  // Fetch employees from API
  useEffect(() => {
    fetchEmployees();
  }, [selectedPeriod]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('[Payroll Management] Fetching data for period:', selectedPeriod);

      // Fetch employees, payroll records, and deployments in parallel
      const [employeesResult, payrollResult, deploymentsResult, attendanceResult, offersResult] = await Promise.all([
        employeeService.getEmployees({ status: 'active' }),
        (async () => {
          const [year, month] = selectedPeriod.split('-');
          const periodStart = `${year}-${month}-01`;
          const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
          const periodEnd = `${year}-${month}-${lastDay}`;
          console.log('[Payroll Management] Fetching payroll records:', { periodStart, periodEnd });
          const result = await payrollService.getPayrollRecords({
            period_start: periodStart,
            period_end: periodEnd
          });
          console.log('[Payroll Management] Payroll records result:', result);
          return result;
        })(),
        employeeDeploymentService.getDeployments({ status: 'active' }),
        (async () => {
          const [year, month] = selectedPeriod.split('-');
          const periodStart = `${year}-${month}-01`;
          const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
          const periodEnd = `${year}-${month}-${lastDay}`;
          return attendanceService.getAttendanceRecords({
            date_from: periodStart,
            date_to: periodEnd
          });
        })(),
        offerLettersService.getOfferLetters()
      ]);

      console.log('[Payroll Management] Employees result:', employeesResult);
      console.log('[Payroll Management] Payroll result:', payrollResult);
      console.log('[Payroll Management] Deployments result:', deploymentsResult);
      console.log('[Payroll Management] Attendance result:', attendanceResult);

      if (employeesResult.success && employeesResult.data) {
        const employeeRows = employeesResult.data;
        const payrollMap = payrollResult.success && payrollResult.data?.length
          ? indexPayrollRecord(payrollResult.data)
          : new Map();

        if (payrollResult.success && payrollResult.data?.length) {
          setPayrollRecords(payrollResult.data);
        } else {
          setPayrollRecords([]);
        }

        const deploymentMapById = new Map();
        if (deploymentsResult.success && deploymentsResult.data) {
          deploymentsResult.data.forEach((deployment) => {
            const dbId = resolveDeploymentEmployeeDbId(deployment, employeeRows);
            if (!dbId) return;
            const existing = deploymentMapById.get(dbId);
            const depDate = new Date(deployment.deployment_start_date || deployment.start_date || 0).getTime();
            const existingDate = existing
              ? new Date(existing.deployment_start_date || existing.start_date || 0).getTime()
              : 0;
            if (!existing || depDate > existingDate) {
              deploymentMapById.set(dbId, deployment);
            }
          });
        }

        const offerCtcMap = offersResult?.success
          ? buildOfferCtcMap(offersResult.data || [], employeeRows)
          : new Map();

        const attendanceEmployeeIds = new Set();
        if (attendanceResult?.success && Array.isArray(attendanceResult.data)) {
          attendanceResult.data.forEach((record) => {
            if (record.employee_id) {
              attendanceEmployeeIds.add(record.employee_id);
            }
          });
        }

        const normalizeStatus = (statusValue) => {
          if (!statusValue) return 'draft';
          if (statusValue === 'processed') return 'locked';
          if (statusValue === 'pending') return 'draft';
          return statusValue;
        };

        const mappedEmployees = employeeRows.map((employee) => {
          const payrollData = findPayrollRecord(payrollMap, employee);
          const deploymentData = deploymentMapById.get(employee.id) || null;
          const amounts = mapPayrollAmounts(
            employee,
            payrollData,
            offerCtcMap.get(employee.id)
          );

          let clientSiteName = null;
          let clientName = null;
          if (payrollData?.client_site_name) {
            clientSiteName = payrollData.client_site_name;
            clientName = payrollData.client_name;
          } else if (deploymentData) {
            clientSiteName = deploymentData.site_name || null;
            clientName = deploymentData.company_name || null;
          }

          const bankAccount = employee.bank_account || employee.bankAccount || '';
          const ifscCode = employee.ifsc_code || employee.ifscCode || '';
          const missingAttendance =
            !attendanceEmployeeIds.has(employee.id) &&
            !attendanceEmployeeIds.has(employee.employee_id);
          const missingDeployment = !deploymentData;
          const missingBankDetails = !bankAccount || !ifscCode;
          const validationIssues = [];
          if (missingAttendance) validationIssues.push('Missing attendance');
          if (missingDeployment) validationIssues.push('Missing deployment');
          if (missingBankDetails) validationIssues.push('Missing bank details');

          const normalizedStatus = payrollData ? normalizeStatus(payrollData.status) : 'draft';
          const payrollEmployeeId = payrollData?.employee_id || null;
          const identifierMismatch =
            !!payrollEmployeeId &&
            payrollEmployeeId !== employee.id &&
            payrollEmployeeId !== employee.employee_id;

          return {
            id: employee.id,
            name: employee.full_name || '',
            employeeId: employee.employee_id || '',
            employeeDbId: employee.id,
            employeeNumber: employee.employee_id || '',
            department: employee.department || '',
            position: employee.position || '',
            email: employee.email || '',
            phone: employee.phone || '',
            salary: parseFloat(employee.salary || 0),
            basicSalary: amounts.basicSalary,
            allowances: amounts.allowances,
            pf: amounts.pf,
            esi: amounts.esi,
            pt: amounts.pt,
            tds: amounts.tds,
            medicalInsurance: amounts.medicalInsurance,
            netPay: amounts.netPay,
            grossSalary: amounts.grossSalary,
            isProjected: amounts.isProjected,
            status: normalizedStatus,
            payPeriodStart: payrollData ? payrollData.pay_period_start : null,
            payPeriodEnd: payrollData ? payrollData.pay_period_end : null,
            processedDate: payrollData ? payrollData.processed_date : null,
            bankAccount,
            ifscCode,
            validationIssues,
            hasValidationIssues: validationIssues.length > 0,
            identifierMismatch,
            payrollEmployeeId,
            employmentStatus: employee.status || 'active',
            clientSiteName,
            clientName,
            _originalEmployee: employee,
            _originalPayrollRecord: payrollData,
            _originalDeployment: deploymentData,
          };
        });

        setEmployees(mappedEmployees);
      } else {
        console.error('[Payroll Management] Failed to load employees:', employeesResult.error);
        setEmployees([]);
        if (employeesResult.error) {
          setError(employeesResult.error);
        }
      }
    } catch (err) {
      console.error('[Payroll Management] Error fetching employees:', err);
      setError('Failed to load employees');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const isRestrictedEmployee = (employee) => {
    return ['inactive', 'suspended'].includes(employee?.employmentStatus);
  };

  const getSelectedEmployeeDetails = (employeeIds) => {
    return employees.filter(emp => employeeIds.includes(emp.id));
  };

  const selectedEmployeeDetails = getSelectedEmployeeDetails(selectedEmployees);
  const hasRestrictedSelectedEmployees = selectedEmployeeDetails.some(isRestrictedEmployee);

  const validationEmployees = employees.filter(emp => emp.hasValidationIssues);
  const validationCounts = validationEmployees.reduce(
    (acc, emp) => {
      if (emp.validationIssues.includes('Missing attendance')) acc.attendance += 1;
      if (emp.validationIssues.includes('Missing deployment')) acc.deployment += 1;
      if (emp.validationIssues.includes('Missing bank details')) acc.bank += 1;
      return acc;
    },
    { attendance: 0, deployment: 0, bank: 0 }
  );

  // Calculate real statutory compliance data from payroll records
  const calculateComplianceData = () => {
    const [year, month] = selectedPeriod.split('-');
    const periodStart = `${year}-${month}-01`;
    const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
    const periodEnd = `${year}-${month}-${lastDay}`;

    // Filter payroll records for the current period
    const periodRecords = payrollRecords.filter(record => {
      if (!record.pay_period_start || !record.pay_period_end) return false;
      return record.pay_period_start >= periodStart && record.pay_period_end <= periodEnd;
    });

    // Calculate PF (Provident Fund) - typically 12% of basic salary each (employee + employer)
    let totalPFEmployee = 0;
    let totalPFEmployer = 0;

    // Calculate TDS (Tax Deducted at Source)
    let totalTDS = 0;

    // Calculate ESI (Employee State Insurance) - typically 0.75% employee, 3.25% employer of gross
    let totalESIEmployee = 0;
    let totalESIEmployer = 0;

    periodRecords.forEach(record => {
      // PF: Use employee_pf from record, assume employer matches (or calculate from basic)
      const basicSalary = parseFloat(record.basic_salary || 0);
      const employeePF = parseFloat(record.employee_pf || 0);
      totalPFEmployee += employeePF;
      // Employer PF typically matches employee PF (12% each)
      totalPFEmployer += employeePF || (basicSalary * 0.12);

      // TDS: Use tax_deduction from record
      totalTDS += parseFloat(record.tax_deduction || 0);

      // ESI: Calculate from gross salary (0.75% employee, 3.25% employer)
      const grossSalary = parseFloat(record.gross_salary || 0);
      if (grossSalary > 0) {
        totalESIEmployee += grossSalary * 0.0075; // 0.75%
        totalESIEmployer += grossSalary * 0.0325; // 3.25%
      }
    });

    // Determine challan status based on period and current date
    const now = new Date();
    const periodDate = new Date(parseInt(year), parseInt(month) - 1, 15); // 15th of the month
    const daysDiff = Math.floor((now - periodDate) / (1000 * 60 * 60 * 24));

    let pfStatus = 'pending';
    let esiStatus = 'pending';
    let tdsStatus = 'pending';

    if (daysDiff > 15) {
      pfStatus = 'overdue';
      esiStatus = 'overdue';
      tdsStatus = 'overdue';
    } else if (daysDiff > 0) {
      pfStatus = 'generated';
      esiStatus = 'generated';
      tdsStatus = 'generated';
    }

    // Format due dates
    const nextMonth = new Date(parseInt(year), parseInt(month), 15);
    const pfDueDate = new Date(nextMonth).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    const esiDueDate = new Date(parseInt(year), parseInt(month), 21).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    const tdsDueDate = new Date(parseInt(year), parseInt(month), 7).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

    return {
      pf: {
        employeeContribution: Math.round(totalPFEmployee),
        employerContribution: Math.round(totalPFEmployer),
        totalAmount: Math.round(totalPFEmployee + totalPFEmployer),
        challanStatus: pfStatus,
        dueDate: pfDueDate
      },
      esi: {
        employeeContribution: Math.round(totalESIEmployee),
        employerContribution: Math.round(totalESIEmployer),
        totalAmount: Math.round(totalESIEmployee + totalESIEmployer),
        challanStatus: esiStatus,
        dueDate: esiDueDate
      },
      tds: {
        employeeContribution: Math.round(totalTDS),
        employerContribution: 0,
        totalAmount: Math.round(totalTDS),
        challanStatus: tdsStatus,
        dueDate: tdsDueDate
      }
    };
  };

  const complianceData = calculateComplianceData();

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    addNotification({
      id: Date.now(),
      type: 'info',
      title: 'Period Changed',
      message: `Payroll period updated to ${period}`,
      timestamp: new Date()
    });
    // Records will be fetched automatically via useEffect
  };

  const handleProcessingModeChange = (mode) => {
    addNotification({
      id: Date.now(),
      type: 'info',
      title: 'Processing Mode',
      message: `Switched to ${mode} processing mode`,
      timestamp: new Date()
    });
  };

  const handleEmployeeUpdate = async (employeeId, updates, employeeRef = null) => {
    try {
      if (isRestrictedEmployee(employeeRef)) {
        addNotification({
          id: Date.now(),
          type: 'warning',
          title: 'Restricted Employee',
          message: 'Inactive or suspended employees cannot be updated in payroll.',
          timestamp: new Date()
        });
        return;
      }

      if (updates?.status && employeeRef?.hasValidationIssues) {
        addNotification({
          id: Date.now(),
          type: 'warning',
          title: 'Validation Required',
          message: 'Resolve validation issues before changing workflow status.',
          timestamp: new Date()
        });
        return;
      }

      const result = await payrollService.updatePayrollRecord(employeeId, updates);
      if (result.success) {
        addNotification({
          id: Date.now(),
          type: 'success',
          title: 'Payroll Updated',
          message: 'Payroll record updated successfully',
          timestamp: new Date()
        });
        // Refresh records
        fetchEmployees();
      } else {
        addNotification({
          id: Date.now(),
          type: 'error',
          title: 'Update Failed',
          message: result.error || 'Failed to update payroll record',
          timestamp: new Date()
        });
      }
    } catch (err) {
      addNotification({
        id: Date.now(),
        type: 'error',
        title: 'Update Failed',
        message: 'Failed to update payroll record',
        timestamp: new Date()
      });
    }
  };

  const handleEmployeeSelect = (employeeIds) => {
    setSelectedEmployees(employeeIds);
  };

  const handleBulkProcess = async (processData) => {
    const employeeIds = Array.isArray(processData) ? processData : (processData.employeeIds || processData);

    if (!employeeIds || employeeIds.length === 0) {
      addNotification({
        id: Date.now(),
        type: 'warning',
        title: 'No Selection',
        message: 'Please select employees to process',
        timestamp: new Date()
      });
      return;
    }

    const selectedDetails = getSelectedEmployeeDetails(employeeIds);
    const restrictedEmployees = selectedDetails.filter(isRestrictedEmployee);
    if (restrictedEmployees.length > 0) {
      addNotification({
        id: Date.now(),
        type: 'warning',
        title: 'Restricted Employees Selected',
        message: `${restrictedEmployees.length} employee(s) are inactive or suspended and cannot be processed.`,
        timestamp: new Date()
      });
      return;
    }

    const missingPayrollRecords = selectedDetails.filter(emp => !emp._originalPayrollRecord?.id);
    if (missingPayrollRecords.length > 0) {
      addNotification({
        id: Date.now(),
        type: 'warning',
        title: 'Missing Payroll Records',
        message: `${missingPayrollRecords.length} employees do not have payroll records.`,
        timestamp: new Date()
      });
      return;
    }

    const invalidEmployees = selectedDetails.filter(emp => emp.hasValidationIssues);
    if (invalidEmployees.length > 0) {
      addNotification({
        id: Date.now(),
        type: 'warning',
        title: 'Validation Failed',
        message: `${invalidEmployees.length} employees have validation issues. Run validation to see details.`,
        timestamp: new Date()
      });
      return;
    }

    const approvable = selectedDetails.filter(emp => emp.status === 'approved');
    if (approvable.length === 0) {
      addNotification({
        id: Date.now(),
        type: 'warning',
        title: 'Approval Required',
        message: 'Only approved payrolls can be locked.',
        timestamp: new Date()
      });
      return;
    }

    const recordIds = approvable
      .map(emp => emp._originalPayrollRecord?.id)
      .filter(Boolean);

    setBulkProcessingStatus({
      status: 'processing',
      message: `Locking payroll for ${recordIds.length} employees...`,
      progress: 0
    });

    try {
      const result = await payrollService.processPayroll(recordIds);

      if (result.success) {
        setBulkProcessingStatus({
          status: 'completed',
          message: `Successfully locked payroll for ${recordIds.length} employees`,
          progress: 100
        });

        addNotification({
          id: Date.now(),
          type: 'success',
          title: 'Bulk Processing Complete',
          message: `Payroll locked for ${recordIds.length} employees`,
          timestamp: new Date()
        });

        // Refresh records
        fetchEmployees();
        setSelectedEmployees([]);

        setTimeout(() => setBulkProcessingStatus(null), 5000);
      } else {
        setBulkProcessingStatus({
          status: 'error',
          message: result.error || 'Failed to process payroll',
          progress: 0
        });
        addNotification({
          id: Date.now(),
          type: 'error',
          title: 'Processing Failed',
          message: result.error || 'Failed to process payroll',
          timestamp: new Date()
        });
      }
    } catch (err) {
      setBulkProcessingStatus({
        status: 'error',
        message: 'Failed to process payroll',
        progress: 0
      });
      addNotification({
        id: Date.now(),
        type: 'error',
        title: 'Processing Failed',
        message: 'Failed to process payroll',
        timestamp: new Date()
      });
    }
  };

  const handleValidationCheck = (employeeIds) => {
    const selectedDetails = getSelectedEmployeeDetails(employeeIds);
    const restrictedEmployees = selectedDetails.filter(isRestrictedEmployee);
    if (restrictedEmployees.length > 0) {
      addNotification({
        id: Date.now(),
        type: 'warning',
        title: 'Restricted Employees Selected',
        message: `${restrictedEmployees.length} employee(s) are inactive or suspended and cannot be validated.`,
        timestamp: new Date()
      });
      return;
    }

    const missingAttendance = selectedDetails.filter(emp => emp.validationIssues.includes('Missing attendance')).length;
    const missingDeployment = selectedDetails.filter(emp => emp.validationIssues.includes('Missing deployment')).length;
    const missingBankDetails = selectedDetails.filter(emp => emp.validationIssues.includes('Missing bank details')).length;
    const totalIssues = selectedDetails.filter(emp => emp.hasValidationIssues).length;

    addNotification({
      id: Date.now(),
      type: totalIssues > 0 ? 'warning' : 'success',
      title: 'Validation Check',
      message: totalIssues > 0
        ? `Issues found for ${totalIssues} employees (Attendance: ${missingAttendance}, Deployment: ${missingDeployment}, Bank: ${missingBankDetails})`
        : `All ${employeeIds.length} employees passed validation`,
      timestamp: new Date()
    });
  };

  const handleApprovalRequest = async (data) => {
    const selectedDetails = getSelectedEmployeeDetails(data.employeeIds || []);
    const restrictedEmployees = selectedDetails.filter(isRestrictedEmployee);
    if (restrictedEmployees.length > 0) {
      addNotification({
        id: Date.now(),
        type: 'warning',
        title: 'Restricted Employees Selected',
        message: `${restrictedEmployees.length} employee(s) are inactive or suspended and cannot be sent for review.`,
        timestamp: new Date()
      });
      return;
    }

    const missingPayrollRecords = selectedDetails.filter(emp => !emp._originalPayrollRecord?.id);
    const invalidEmployees = selectedDetails.filter(emp => emp.hasValidationIssues);
    const draftEmployees = selectedDetails.filter(emp => emp.status === 'draft');

    if (missingPayrollRecords.length > 0) {
      addNotification({
        id: Date.now(),
        type: 'warning',
        title: 'Missing Payroll Records',
        message: `${missingPayrollRecords.length} employees do not have payroll records.`,
        timestamp: new Date()
      });
      return;
    }

    if (invalidEmployees.length > 0) {
      addNotification({
        id: Date.now(),
        type: 'warning',
        title: 'Validation Failed',
        message: `${invalidEmployees.length} employees have validation issues.`,
        timestamp: new Date()
      });
      return;
    }

    if (draftEmployees.length === 0) {
      addNotification({
        id: Date.now(),
        type: 'info',
        title: 'No Draft Payrolls',
        message: 'Only draft payrolls can be sent for review.',
        timestamp: new Date()
      });
      return;
    }

    try {
      await Promise.all(
        draftEmployees.map(emp =>
          payrollService.updatePayrollRecord(emp._originalPayrollRecord.id, { status: 'reviewed' })
        )
      );
      addNotification({
        id: Date.now(),
        type: 'success',
        title: 'Review Requested',
        message: `Moved ${draftEmployees.length} payroll(s) to Reviewed.`,
        timestamp: new Date()
      });
      fetchEmployees();
      setSelectedEmployees([]);
    } catch (error) {
      addNotification({
        id: Date.now(),
        type: 'error',
        title: 'Request Failed',
        message: error?.message || 'Failed to request approval',
        timestamp: new Date()
      });
    }
  };

  const handleApproveSelected = async (employeeIds) => {
    const selectedDetails = getSelectedEmployeeDetails(employeeIds || []);
    const restrictedEmployees = selectedDetails.filter(isRestrictedEmployee);
    if (restrictedEmployees.length > 0) {
      addNotification({
        id: Date.now(),
        type: 'warning',
        title: 'Restricted Employees Selected',
        message: `${restrictedEmployees.length} selected employee(s) are inactive or suspended and cannot be approved.`,
        timestamp: new Date()
      });
      return;
    }

    const missingPayrollRecords = selectedDetails.filter(emp => !emp._originalPayrollRecord?.id);
    const invalidEmployees = selectedDetails.filter(emp => emp.hasValidationIssues);
    const reviewedEmployees = selectedDetails.filter(emp => emp.status === 'reviewed');

    if (missingPayrollRecords.length > 0) {
      addNotification({
        id: Date.now(),
        type: 'warning',
        title: 'Missing Payroll Records',
        message: `${missingPayrollRecords.length} employees do not have payroll records.`,
        timestamp: new Date()
      });
      return;
    }

    if (invalidEmployees.length > 0) {
      addNotification({
        id: Date.now(),
        type: 'warning',
        title: 'Validation Failed',
        message: `${invalidEmployees.length} employees have validation issues.`,
        timestamp: new Date()
      });
      return;
    }

    if (reviewedEmployees.length === 0) {
      addNotification({
        id: Date.now(),
        type: 'info',
        title: 'No Reviewed Payrolls',
        message: 'Only reviewed payrolls can be approved.',
        timestamp: new Date()
      });
      return;
    }

    try {
      await Promise.all(
        reviewedEmployees.map(emp =>
          payrollService.updatePayrollRecord(emp._originalPayrollRecord.id, { status: 'approved' })
        )
      );
      addNotification({
        id: Date.now(),
        type: 'success',
        title: 'Approved',
        message: `Approved ${reviewedEmployees.length} payroll(s).`,
        timestamp: new Date()
      });
      fetchEmployees();
      setSelectedEmployees([]);
    } catch (error) {
      addNotification({
        id: Date.now(),
        type: 'error',
        title: 'Approval Failed',
        message: error?.message || 'Failed to approve payrolls',
        timestamp: new Date()
      });
    }
  };

  const handleGenerateChallan = (type) => {
    addNotification({
      id: Date.now(),
      type: 'success',
      title: 'Challan Generated',
      message: `${type.toUpperCase()} challan downloaded for ${selectedPeriod}`,
      timestamp: new Date()
    });
  };

  const handleGeneratePayslip = async (employeeId, options) => {
    try {
      // Find the employee
      const employee = employees.find(emp => emp.id === employeeId);

      if (!employee) {
        addNotification({
          id: Date.now(),
          type: 'error',
          title: 'Employee Not Found',
          message: 'Employee not found',
          timestamp: new Date()
        });
        return;
      }

      // Check if payroll record exists
      const payrollRecord = employee._originalPayrollRecord;

      if (!payrollRecord) {
        addNotification({
          id: Date.now(),
          type: 'warning',
          title: 'No Payroll Record',
          message: `No payroll record found for ${employee.name}. Please create a payroll record first.`,
          timestamp: new Date()
        });
        return;
      }

      if (isRestrictedEmployee(employee)) {
        addNotification({
          id: Date.now(),
          type: 'warning',
          title: 'Restricted Employee',
          message: `${employee.name} is inactive or suspended and cannot generate a payslip.`,
          timestamp: new Date()
        });
        return;
      }

      // Get auth token from localStorage
      let token = localStorage.getItem('erp_auth_token') || localStorage.getItem('auth_token');

      // Fallback for local authentication (mock)
      if (!token) {
        try {
          const localAuth = localStorage.getItem('laborpro_local_auth');
          if (localAuth) {
            const authData = JSON.parse(localAuth);
            token = authData.session?.access_token;
          }
        } catch (e) {
          console.error('[Payroll Management] Error parsing local auth:', e);
        }
      }

      if (!token) {
        addNotification({
          id: Date.now(),
          type: 'error',
          title: 'Authentication Required',
          message: 'Please log in again to generate payslip',
          timestamp: new Date()
        });
        return;
      }

      // Get API base URL from environment or use default
      // Get API base URL from environment or use default
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ||
        import.meta.env.VITE_ERP_API_URL ||
        (window.location.hostname === 'localhost' ? `http://${window.location.hostname}:8000/api` : window.location.origin + '/api');
      console.log('[Payroll Management] Generate Payslip - API_BASE_URL:', API_BASE_URL);

      // Fetch payslip HTML using authenticated request
      const payslipUrl = `${API_BASE_URL}/payroll/${payrollRecord.id}/payslip?format=html`;
      console.log('[Payroll Management] Generate Payslip - URL:', payslipUrl);
      console.log('[Payroll Management] Generate Payslip - Payroll Record ID:', payrollRecord.id);

      const response = await fetch(payslipUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8'
        }
      });

      console.log('[Payroll Management] Generate Payslip - Response status:', response.status);
      console.log('[Payroll Management] Generate Payslip - Response Content-Type:', response.headers.get('Content-Type'));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Payroll Management] Payslip generation error:', errorText);
        console.error('[Payroll Management] Error response status:', response.status);
        throw new Error(`Failed to generate payslip: ${response.status} ${response.statusText}`);
      }

      const htmlContent = await response.text();
      console.log('[Payroll Management] Generate Payslip - Response content length:', htmlContent.length);
      console.log('[Payroll Management] Generate Payslip - Response preview (first 200 chars):', htmlContent.substring(0, 200));
      console.log('[Payroll Management] Generate Payslip - Is HTML?', htmlContent.trim().startsWith('<!DOCTYPE') || htmlContent.trim().startsWith('<html'));
      console.log('[Payroll Management] Generate Payslip - Is JSON?', htmlContent.trim().startsWith('{') || htmlContent.trim().startsWith('['));

      if (htmlContent.trim().startsWith('{') || htmlContent.trim().startsWith('[')) {
        console.error('[Payroll Management] ERROR: Received JSON instead of HTML!');
        console.error('[Payroll Management] Full response:', htmlContent);
      }

      // Open new window and write HTML content
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(htmlContent);
        newWindow.document.close();
        addNotification({
          id: Date.now(),
          type: 'success',
          title: 'Payslip Generated',
          message: `Payslip generated for ${employee.name}`,
          timestamp: new Date()
        });
      } else {
        addNotification({
          id: Date.now(),
          type: 'error',
          title: 'Popup Blocked',
          message: 'Please allow popups to view the payslip',
          timestamp: new Date()
        });
      }
    } catch (err) {
      console.error('Error generating payslip:', err);
      addNotification({
        id: Date.now(),
        type: 'error',
        title: 'Payslip Generation Failed',
        message: err.message || 'Failed to generate payslip',
        timestamp: new Date()
      });
    }
  };

  const handleBulkGeneratePayslips = (options) => {
    addNotification({
      id: Date.now(),
      type: 'success',
      title: 'Bulk Payslips Generated',
      message: `Generated payslips for all ${employees.length} employees`,
      timestamp: new Date()
    });
  };

  const addNotification = (notification) => {
    setNotifications(prev => [notification, ...prev.slice(0, 4)]);
  };

  const handleNotificationDismiss = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation
        onSidebarToggle={handleSidebarToggle}
        user={user}
        notifications={notifications}
      />

      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className={`pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="flex min-w-0">
          {/* Main Content */}
          <div className="flex-1 p-6 min-w-0 max-w-full overflow-hidden">
            <BreadcrumbNavigation />

            <div className="mb-8 flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Payroll Management</h1>
                <p className="text-muted-foreground">
                  Streamline salary processing and statutory compliance for your workforce
                </p>
              </div>
              <Button onClick={() => { setEditingPayrollRecord(null); setShowCreatePayrollModal(true); }} iconName="Plus" iconPosition="left">
                Add Payroll Record
              </Button>
            </div>

            <PayrollPeriodSelector
              selectedPeriod={selectedPeriod}
              onPeriodChange={handlePeriodChange}
              processingStatus={processingStatus}
              onProcessingModeChange={handleProcessingModeChange}
            />

            <PayrollWorkflowBanner counts={workflowCounts} />

            <ValidationSummary
              validationEmployees={validationEmployees}
              validationCounts={validationCounts}
              selectedEmployees={selectedEmployees}
              onSelectAllAffected={() => setSelectedEmployees(validationEmployees.map((emp) => emp.id))}
              onSelectEmployee={(id) => {
                if (!selectedEmployees.includes(id)) {
                  setSelectedEmployees([...selectedEmployees, id]);
                }
              }}
            />

            <PayrollActionBar
              selectedCount={selectedEmployees.length}
              processingStatus={bulkProcessingStatus}
              hasRestrictedSelectedEmployees={hasRestrictedSelectedEmployees}
              onValidate={() => handleValidationCheck(selectedEmployees)}
              onMarkReviewed={() => handleApprovalRequest({ employeeIds: selectedEmployees })}
              onApprove={() => handleApproveSelected(selectedEmployees)}
              onLockAndProcess={() => handleBulkProcess({ employeeIds: selectedEmployees })}
              onGeneratePayslip={() => {
                if (selectedEmployees.length === 1) {
                  handleGeneratePayslip(selectedEmployees[0], {});
                }
              }}
              onBulkPayslips={() => handleBulkGeneratePayslips({})}
            />

            {loading ? (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <p className="text-muted-foreground">Loading payroll records...</p>
              </div>
            ) : error ? (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <p className="text-error">{error}</p>
                <button
                  onClick={fetchEmployees}
                  className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
                >
                  Retry
                </button>
              </div>
            ) : employees.length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <p className="text-muted-foreground">No employees found.</p>
                <p className="text-sm text-muted-foreground mt-2">Add employees to get started with payroll management.</p>
              </div>
            ) : (
              <PayrollTable
                employees={employees}
                onEmployeeUpdate={handleEmployeeUpdate}
                onBulkProcess={handleBulkProcess}
                selectedEmployees={selectedEmployees}
                onEmployeeSelect={handleEmployeeSelect}
                hasRestrictedSelection={hasRestrictedSelectedEmployees}
                onEditPayroll={(employee) => {
                  const record = employee._originalPayrollRecord;
                  if (record?.id) {
                    setEditingPayrollRecord(record);
                    setPrefillEmployeeId(null);
                  } else {
                    setEditingPayrollRecord(null);
                    setPrefillEmployeeId(employee._originalEmployee?.id || employee.id);
                  }
                  setShowCreatePayrollModal(true);
                }}
                onViewEarnings={(employee) => {
                  setSelectedEmployeeForBreakdown(employee);
                  setShowEarningsModal(true);
                }}
                onViewPayments={(employee) => {
                  setSelectedEmployeeForBreakdown(employee);
                  setShowPaymentsModal(true);
                }}
                onViewDetailedPayslip={(employee) => {
                  setSelectedEmployeeForBreakdown(employee);
                  setShowDetailedPayslipModal(true);
                }}
              />
            )}
          </div>

          {/* Statutory Compliance Sidebar */}
          <div className="hidden lg:block shrink-0 p-4 xl:p-6 pl-0">
            <StatutoryComplianceSidebar
              complianceData={complianceData}
              selectedPeriod={selectedPeriod}
              payrollRecordCount={payrollRecords.length}
              onChallanGenerated={handleGenerateChallan}
            />
          </div>
        </div>
      </div>

      {/* Create/Edit Payroll Record Modal */}
      <CreatePayrollRecordModal
        isOpen={showCreatePayrollModal}
        onClose={() => {
          console.log('[Payroll Management] Closing payroll modal');
          setShowCreatePayrollModal(false);
          setEditingPayrollRecord(null);
          setPrefillEmployeeId(null);
        }}
        payrollRecord={editingPayrollRecord}
        prefillEmployeeId={prefillEmployeeId}
        onSave={() => {
          console.log('[Payroll Management] Payroll record saved, refreshing data');
          setShowCreatePayrollModal(false);
          setEditingPayrollRecord(null);
          setPrefillEmployeeId(null);
          fetchEmployees(); // Reload data
        }}
      />

      <NotificationToast
        notifications={notifications}
        onDismiss={handleNotificationDismiss}
        position="top-right"
        autoHideDuration={5000}
      />

      {/* Earnings Breakdown Modal */}
      {showEarningsModal && selectedEmployeeForBreakdown && selectedEmployeeForBreakdown._originalPayrollRecord && (
        <EarningsBreakdownModal
          isOpen={showEarningsModal}
          onClose={() => {
            setShowEarningsModal(false);
            setSelectedEmployeeForBreakdown(null);
          }}
          payrollRecordId={selectedEmployeeForBreakdown._originalPayrollRecord.id}
          employeeName={selectedEmployeeForBreakdown.name}
          onUpdate={() => {
            fetchEmployees();
          }}
        />
      )}

      {/* Payments Breakdown Modal */}
      {showPaymentsModal && selectedEmployeeForBreakdown && selectedEmployeeForBreakdown._originalPayrollRecord && (
        <PaymentsBreakdownModal
          isOpen={showPaymentsModal}
          onClose={() => {
            setShowPaymentsModal(false);
            setSelectedEmployeeForBreakdown(null);
          }}
          payrollRecordId={selectedEmployeeForBreakdown._originalPayrollRecord.id}
          employeeName={selectedEmployeeForBreakdown.name}
          netSalary={selectedEmployeeForBreakdown.netPay}
          onUpdate={() => {
            fetchEmployees();
          }}
        />
      )}

      {/* Detailed Payslip Modal */}
      {showDetailedPayslipModal && selectedEmployeeForBreakdown && selectedEmployeeForBreakdown._originalPayrollRecord && (
        <DetailedPayslipModal
          isOpen={showDetailedPayslipModal}
          onClose={() => {
            setShowDetailedPayslipModal(false);
            setSelectedEmployeeForBreakdown(null);
          }}
          payrollRecordId={selectedEmployeeForBreakdown._originalPayrollRecord.id}
          employeeName={selectedEmployeeForBreakdown.name}
        />
      )}
    </div>
  );
};

export default PayrollManagement;
